"use client";

import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { facilityTypes } from "../data";
import { Facility } from "../types";
interface FacilityTableProps {
  facilities: Facility[];
  onEdit: (facility: Facility) => void;
  onDelete: (facility: Facility) => void;
  onAdd: () => void;
}

export function FacilityTable({
  facilities,
  onEdit,
  onDelete,
  onAdd
}: FacilityTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<Facility | null>(
    null
  );

  const getFacilityTypeLabel = (value: string) => {
    const type = facilityTypes.find((type) => type.value === value);
    return type ? type.label : value;
  };

  const columns: ColumnDef<Facility>[] = [
    {
      accessorKey: "searchableText",
      header: "",
      cell: () => null,
      enableHiding: false,
      enableSorting: false,
      size: 0
    },
    {
      accessorKey: "location",
      header: "Standort"
    },
    {
      accessorKey: "address",
      header: "Adresse"
    },
    {
      accessorKey: "state",
      header: "Bundesland"
    },
    {
      accessorKey: "facility_type",
      header: "Einrichtungstyp",
      cell: ({ row }) => {
        const facilityType = row.getValue("facility_type") as string;
        return getFacilityTypeLabel(facilityType);
      }
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreHorizontal size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(row.original);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Bearbeiten
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setFacilityToDelete(row.original);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  const handleDelete = () => {
    if (facilityToDelete) {
      onDelete(facilityToDelete);
      setDeleteDialogOpen(false);
      setFacilityToDelete(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Einrichtungen</h3>
        <Button onClick={onAdd}>Einrichtung hinzufügen</Button>
      </div>

      {facilities.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Keine Einrichtungen gefunden
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={facilities.map((facility) => ({
            ...facility,
            searchableText: `${facility.location} ${facility.address} ${facility.state || ""} ${getFacilityTypeLabel(facility.facility_type)}`
          }))}
          searchKey="searchableText"
          filterPlaceholder="Einrichtungen filtern..."
          initialColumnVisibility={{ searchableText: false }}
        />
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Einrichtung löschen</DialogTitle>
            <DialogDescription>
              Bist du sicher, dass du diese Einrichtung löschen möchtest? Diese
              Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
